import { defaultBusiness as NAP } from "@/lib/site-config";
// Site-wide SEO helpers — produces canonical, OG, Twitter, geo and
// JSON-LD payloads in one place so every route stays consistent.
//
// Usage in a route:
//   head: () => ({
//     ...seoHead({
//       path: "/mot",
//       title: "MOT Testing East London | Classes 1–7 | Woodseer Autos, E14",
//       description: "...",
//       image: SITE_OG_IMAGE,
//       breadcrumbs: [
//         { name: "Home", path: "/" },
//         { name: "MOT Testing", path: "/mot" },
//       ],
//       jsonLd: [{ ... }],
//     }),
//   })

export const SITE_ORIGIN = "https://woodseerautos.co.uk";
export const SITE_NAME = "Woodseer Autos";
export const SITE_OG_IMAGE = `${SITE_ORIGIN}/og-image.jpg`;

// Set this to the verification code value from
// Search Console → Settings → Ownership verification → HTML tag.
// Leave empty if the site is verified another way (e.g. as a Domain
// property via DNS) — the meta tag is then omitted from the head.
export const GSC_VERIFICATION_CODE = "";

// Bing Webmaster Tools verification — paste the value from
// Bing Webmaster → Site → Verify ownership → HTML Meta Tag.
// Leave empty to omit the tag entirely.
export const BING_VERIFICATION_CODE = "";

// Geo coordinates for the workshop yard (14 Copenhagen Place, E14 7DF).
export const WORKSHOP_GEO = { latitude: 51.514512, longitude: -0.029928 };

export interface BreadcrumbItem {
  name: string;
  /** Path beginning with `/` (e.g. "/areas/poplar"). */
  path: string;
}

export interface SeoHeadOptions {
  /** Path beginning with `/` (canonical URL is `${SITE_ORIGIN}${path}`). */
  path: string;
  title: string;
  description: string;
  /** Absolute URL to OG/Twitter image. Defaults to SITE_OG_IMAGE. */
  image?: string;
  /** Override `og:type` (defaults to "website"). */
  ogType?: string;
  /** When true, emits noindex/nofollow robots meta. */
  noindex?: boolean;
  /** Optional breadcrumb trail — emitted as BreadcrumbList JSON-LD. */
  breadcrumbs?: BreadcrumbItem[];
  /** Additional JSON-LD payloads to attach. */
  jsonLd?: Record<string, unknown>[];
}

export function canonicalUrl(path: string): string {
  if (!path.startsWith("/")) path = `/${path}`;
  // Drop trailing slash except for root
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return `${SITE_ORIGIN}${path}`;
}

export function breadcrumbJsonLd(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: canonicalUrl(item.path),
    })),
  };
}

/**
 * Build the meta + scripts + links payload for a route's `head()`.
 * Returns an object that can be spread into `head()`'s return value.
 */
export function seoHead(opts: SeoHeadOptions): {
  meta: { name?: string; property?: string; content?: string; charSet?: string }[];
  links: { rel: string; href: string }[];
  scripts: { type: string; children: string }[];
} {
  const url = canonicalUrl(opts.path);
  const image = opts.image || SITE_OG_IMAGE;
  const ogType = opts.ogType || "website";

  const meta: { name?: string; property?: string; content?: string; charSet?: string }[] = [
    { title: opts.title } as never,
    { name: "description", content: opts.description },
    {
      name: "robots",
      content: opts.noindex
        ? "noindex, nofollow"
        : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
    },
    // Open Graph
    { property: "og:type", content: ogType },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:title", content: opts.title },
    { property: "og:description", content: opts.description },
    { property: "og:url", content: url },
    { property: "og:locale", content: "en_GB" },
    { property: "og:image", content: image },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: opts.title },
    { name: "twitter:description", content: opts.description },
    { name: "twitter:image", content: image },
    // Geo targeting (East London / E14)
    { name: "geo.region", content: "GB-ENG" },
    { name: "geo.placename", content: "London, Tower Hamlets, E14" },
    { name: "geo.position", content: `${WORKSHOP_GEO.latitude};${WORKSHOP_GEO.longitude}` },
    { name: "ICBM", content: `${WORKSHOP_GEO.latitude}, ${WORKSHOP_GEO.longitude}` },
  ];

  if (GSC_VERIFICATION_CODE) {
    meta.push({ name: "google-site-verification", content: GSC_VERIFICATION_CODE });
  }

  const links: { rel: string; href: string }[] = [
    { rel: "canonical", href: url },
  ];

  const scripts: { type: string; children: string }[] = [];

  if (opts.breadcrumbs && opts.breadcrumbs.length > 0) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify(breadcrumbJsonLd(opts.breadcrumbs)),
    });
  }

  if (opts.jsonLd) {
    for (const ld of opts.jsonLd) {
      scripts.push({ type: "application/ld+json", children: JSON.stringify(ld) });
    }
  }

  return { meta, links, scripts };
}

/** Canonical sameAs profile URLs for the business entity. */
export const SAME_AS_URLS = [
  "https://www.instagram.com/woodseerautos/",
  "https://linktr.ee/woodseerautos",
  "https://share.google/ex6hlV5y3QnHwzg2P",
];

/** Site-wide Organization schema — included once at the root. */
export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_ORIGIN}/#business`,
  name: SITE_NAME,
  alternateName: "Woodseer Autos",
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/logo.png`,
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: NAP.phoneHref.replace("tel:", ""),
      email: "info@woodseerautos.co.uk",
      contactType: "customer service",
      areaServed: "GB",
      availableLanguage: "English",
      hoursAvailable: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:00",
        closes: "18:00",
      },
    },
    {
      "@type": "ContactPoint",
      email: "woodseerautos@gmail.com",
      contactType: "administrative support",
      areaServed: "GB",
      availableLanguage: "English",
    },
  ],
  sameAs: SAME_AS_URLS,
};

/** Build a LocalBusiness + AutoRepair schema for the homepage. */
export function localBusinessSchema(opts: {
  ratingValue?: string;
  reviewCount?: string;
  openingHours?: { dayOfWeek: string[]; opens: string; closes: string }[];
  /** Admin-edited NAP overrides — when present, these win over hardcoded defaults. */
  nap?: {
    streetAddress?: string;
    addressLocality?: string;
    postalCode?: string;
    addressCountry?: string;
    telephone?: string;
    email?: string;
    legalName?: string;
  };
  /** Admin-edited service coverage list — Place names like "Mile End". */
  serviceCoverage?: string[];
  /** Admin-edited service catalog — drives hasOfferCatalog. */
  serviceCatalog?: string[];
} = {}) {
  const nap = opts.nap || {};
  const coverage = (opts.serviceCoverage && opts.serviceCoverage.length > 0)
    ? opts.serviceCoverage
    : [
        "Poplar", "Canary Wharf", "Isle of Dogs", "Limehouse", "Docklands", "Tower Hamlets",
        "Whitechapel", "Brick Lane", "Bow", "Mile End", "Bethnal Green", "Wapping",
        "Stratford", "Stepney", "Shadwell", "Aldgate", "Canning Town", "Blackwall", "E14",
      ];
  const catalog = (opts.serviceCatalog && opts.serviceCatalog.length > 0)
    ? opts.serviceCatalog
    : [
        "MOT Testing", "Car Servicing", "Engine Diagnostics", "Mechanical Repairs",
        "Bodywork & Paint Repair", "Brakes", "Tyres", "Clutch Repairs", "Suspension Repairs",
      ];
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "AutoRepair"],
    "@id": `${SITE_ORIGIN}/#business`,
    name: SITE_NAME,
    alternateName: "Woodseer Autos",
    legalName: nap.legalName || "Woodseer Autos Ltd",
    description:
      "Independent garage in East London offering MOT testing (classes 1–7), car servicing, engine diagnostics, mechanical repairs and bodywork. Honest mechanics, fair pricing, clear advice.",
    url: SITE_ORIGIN,
    telephone: nap.telephone || NAP.phoneHref.replace("tel:", ""),
    email: nap.email || "info@woodseerautos.co.uk",
    image: SITE_OG_IMAGE,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_ORIGIN}/logo.png`,
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: nap.streetAddress || "16 Pixley Street",
      addressLocality: nap.addressLocality || "Poplar",
      addressRegion: "London",
      postalCode: nap.postalCode || "E14 7DF",
      addressCountry: nap.addressCountry || "GB",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: WORKSHOP_GEO.latitude,
      longitude: WORKSHOP_GEO.longitude,
    },
    hasMap:
      "https://www.google.com/maps?q=14+Copenhagen+Place+London+E14+7DF",
    openingHoursSpecification:
      opts.openingHours && opts.openingHours.length > 0
        ? opts.openingHours.map((h) => ({
            "@type": "OpeningHoursSpecification",
            dayOfWeek: h.dayOfWeek,
            opens: h.opens,
            closes: h.closes,
          }))
        : [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
              opens: "09:00",
              closes: "18:00",
            },
          ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: opts.ratingValue || "4.4",
      reviewCount: opts.reviewCount || "116",
      bestRating: "5",
      worstRating: "1",
    },
    priceRange: "££",
    currenciesAccepted: "GBP",
    paymentAccepted: "Cash, Credit Card, Debit Card",
    areaServed: coverage.map((name) => ({ "@type": "Place", name: `${name}, London` })),
    knowsAbout: catalog,
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: nap.telephone || NAP.phoneHref.replace("tel:", ""),
        email: nap.email || "info@woodseerautos.co.uk",
        contactType: "customer service",
        areaServed: "GB",
        availableLanguage: "English",
      },
      {
        "@type": "ContactPoint",
        email: "woodseerautos@gmail.com",
        contactType: "administrative support",
        areaServed: "GB",
        availableLanguage: "English",
      },
    ],
    sameAs: SAME_AS_URLS,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Garage Services",
      itemListElement: catalog.map((name) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name },
      })),
    },
  };
}

/**
 * Adapt a ComplianceContent payload into the shape `localBusinessSchema()` expects.
 * Keeps route files free of payload-shape boilerplate.
 */
export function complianceToBusinessOpts(c: {
  streetAddress?: string;
  addressLocality?: string;
  postalCode?: string;
  addressCountry?: string;
  legalName?: string;
  serviceCoverage?: string[];
  serviceCatalog?: string[];
  openingHours?: { days: string[]; opens: string; closes: string }[];
} | null | undefined) {
  if (!c) return {};
  return {
    nap: {
      streetAddress: c.streetAddress,
      addressLocality: c.addressLocality,
      postalCode: c.postalCode,
      addressCountry: c.addressCountry,
      legalName: c.legalName,
    },
    serviceCoverage: c.serviceCoverage,
    serviceCatalog: c.serviceCatalog,
    openingHours: (c.openingHours || []).map((h) => ({
      dayOfWeek: h.days,
      opens: h.opens,
      closes: h.closes,
    })),
  };
}

/**
 * Build a Service JSON-LD payload pointing at the homepage business node.
 */
export function serviceSchema(opts: {
  name: string;
  serviceType: string;
  description: string;
  areaServed?: string | string[];
  url?: string;
  offers?: { priceCurrency?: string; price?: string; description?: string };
}) {
  const areas = opts.areaServed
    ? (Array.isArray(opts.areaServed) ? opts.areaServed : [opts.areaServed])
    : ["East London, E14"];
  const ld: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: opts.name,
    serviceType: opts.serviceType,
    description: opts.description,
    provider: { "@id": `${SITE_ORIGIN}/#business` },
    areaServed: areas.map((name) => ({ "@type": "Place", name })),
  };
  if (opts.url) ld.url = opts.url;
  if (opts.offers) {
    ld.offers = {
      "@type": "Offer",
      priceCurrency: opts.offers.priceCurrency || "GBP",
      ...(opts.offers.price ? { price: opts.offers.price } : {}),
      ...(opts.offers.description ? { description: opts.offers.description } : {}),
      availability: "https://schema.org/InStock",
    };
  }
  return ld;
}
